/**
 * DemoBanner — thin persistent strip that sits above every routed app
 * when YORIK_DEMO_MODE=1. Three jobs:
 *
 *   1. Constant reminder that this is a SHARED demo account — other
 *      visitors see what you create.
 *   2. Live countdown to the next state reset so people know how long
 *      their test data sticks around (and trigger a refetch right
 *      after rollover so the UI doesn't show stale data from a wiped
 *      backend).
 *   3. Discourage real personal info: short, blunt, no dismiss button.
 *
 * The component is rendered by AuthGate after a successful login, so
 * non-demo installs never see it.
 */

import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import type { DemoInfo } from "@/lib/api";

interface Props {
  demo: DemoInfo;
}

function formatRemaining(msRemaining: number): string {
  if (msRemaining <= 0) return "now";
  const totalSec = Math.floor(msRemaining / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min === 0) return `${sec}s`;
  if (sec === 0) return `${min} min`;
  return `${min} min ${sec}s`;
}

export function DemoBanner({ demo }: Props) {
  const resetAt = new Date(demo.next_reset_iso).getTime();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  // When the next-reset boundary passes, the backend has wiped state
  // (or is in the middle of doing so). A full page reload pulls fresh
  // data + a fresh `next_reset_iso` so the countdown re-anchors. Plus
  // the user gets the "session expired" login screen if their cookie
  // was invalidated by the rebuild.
  useEffect(() => {
    if (now >= resetAt) {
      // Tiny grace period so we don't reload mid-tick race.
      const t = setTimeout(() => window.location.reload(), 500);
      return () => clearTimeout(t);
    }
  }, [now, resetAt]);

  const remaining = formatRemaining(resetAt - now);

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-[700] w-full bg-amber-500/90 text-amber-950 text-[12px] leading-tight"
    >
      <div className="max-w-screen-2xl mx-auto px-3 py-1.5 flex items-center gap-2 flex-wrap">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
        <span className="font-semibold uppercase tracking-wider text-[11px]">
          Demo mode
        </span>
        <span className="opacity-90">
          shared account · next reset in <strong>{remaining}</strong> · everything
          you type will be visible to the next tester until the reset · don't
          enter real personal info
        </span>
      </div>
    </div>
  );
}
